import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Card,
} from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import {
  ScheduleCampaignFullInitialSendMutation,
  ScheduleCampaignFullInitialSendMutationVariables,
  ScheduleCampaignPartialInitialSendMutation,
  ScheduleCampaignPartialInitialSendMutationVariables,
  StartCampaignFullInitialSendMutation,
  StartCampaignFullInitialSendMutationVariables,
  StartCampaignPartialInitialSendMutation,
  StartCampaignPartialInitialSendMutationVariables,
} from "~frontend/__generated__/graphql";
import {
  RhfDateTimePicker,
  RhfSelect,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfNumberField } from "~frontend/components/forms/react-hook-form-components/RhfNumberField";
import {
  GET_CAMPAIGN_FOR_VIEW,
  SCHEDULE_CAMPAIGN_FULL_INITIAL_SEND,
  SCHEDULE_CAMPAIGN_PARTIAL_INITIAL_SEND,
  START_CAMPAIGN_FULL_INITIAL_SEND,
  START_CAMPAIGN_PARTIAL_INITIAL_SEND,
} from "~frontend/graphql/campaigns";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import { SendSummaryCard } from "~frontend/routes/subaccounts/campaignView/initialSend/SendSummaryCard";
import {
  useTextingHours,
  createScheduleForSchema,
} from "~frontend/util/textingHours";
import useMutation from "~frontend/util/useMutation";

import { CampaignForView } from "../types";

function createPickAWinnerSchema(
  textingHoursStart: string,
  textingHoursEnd: string,
  textingHoursTimezone: string,
  nLeftToSend: number
) {
  return z
    .object({
      sendingOption: z.string().min(1, "Please select an option"),
      qty: z.coerce
        .number()
        .int("must be a whole number")
        .min(1, "Must be at least 1")
        .optional(),
      scheduleFor: createScheduleForSchema({
        start: textingHoursStart,
        end: textingHoursEnd,
        timezone: textingHoursTimezone,
      }),
    })
    .superRefine((data, ctx) => {
      if (!data.sendingOption.endsWith(":partial")) {
        return;
      }
      if (!data.qty) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["qty"],
          message: "Must be at least 1",
        });
      } else if (data.qty > nLeftToSend) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["qty"],
          message: `Only ${nLeftToSend.toLocaleString()} left to send`,
        });
      }
    });
}

type PickAWinnerFormOutput = z.infer<
  ReturnType<typeof createPickAWinnerSchema>
>;

export function PickAWinnerDialog({
  campaign,
  handleClose,
}: {
  campaign: CampaignForView;
  handleClose: () => void;
}): ReactElement {
  const subaccount = useSubaccountAdminConfig()!;
  const variants = campaign.scriptVariants.filter(v => !v.deletedAt);

  const textingHours = {
    start: campaign.textingHoursStart ?? subaccount.textingHoursStart,
    end: campaign.textingHoursEnd ?? subaccount.textingHoursEnd,
    timezone: campaign.textingHoursTimezone ?? subaccount.textingHoursTimezone,
  };
  const { isInTextingHours } = useTextingHours(textingHours);
  const nLeftToSend = campaign.quickStats
    ? campaign.quickStats.contactCount - campaign.quickStats.initialSentCount
    : 0;
  const schema = useMemo(
    () =>
      createPickAWinnerSchema(
        textingHours.start,
        textingHours.end,
        textingHours.timezone,
        nLeftToSend
      ),
    [textingHours.start, textingHours.end, textingHours.timezone, nLeftToSend]
  );
  const [sendFull] = useMutation<
    StartCampaignFullInitialSendMutation,
    StartCampaignFullInitialSendMutationVariables
  >(START_CAMPAIGN_FULL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to start send",
  });

  const [sendPartial] = useMutation<
    StartCampaignPartialInitialSendMutation,
    StartCampaignPartialInitialSendMutationVariables
  >(START_CAMPAIGN_PARTIAL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to start partial send",
  });

  const [scheduleFull] = useMutation<
    ScheduleCampaignFullInitialSendMutation,
    ScheduleCampaignFullInitialSendMutationVariables
  >(SCHEDULE_CAMPAIGN_FULL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to schedule send",
  });

  const [schedulePartial] = useMutation<
    ScheduleCampaignPartialInitialSendMutation,
    ScheduleCampaignPartialInitialSendMutationVariables
  >(SCHEDULE_CAMPAIGN_PARTIAL_INITIAL_SEND, {
    refetchQueries: [GET_CAMPAIGN_FOR_VIEW],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to schedule partial send",
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    reValidateMode: "onBlur",
  });

  const sendingOption = useWatch({ control, name: "sendingOption" });
  const isPartial = sendingOption?.endsWith(":partial") ?? false;

  async function onSubmit(input: PickAWinnerFormOutput) {
    const [variantId, sendType] = input.sendingOption.split(":");

    if (input.scheduleFor) {
      if (sendType === "partial" && input.qty !== undefined) {
        await schedulePartial({
          variables: {
            input: {
              campaignId: campaign.id,
              qty: input.qty,
              scheduleFor: input.scheduleFor,
              sendWithVariantId: variantId,
            },
          },
        });
      } else {
        await scheduleFull({
          variables: {
            input: {
              campaignId: campaign.id,
              scheduleFor: input.scheduleFor,
              sendWithVariantId: variantId,
            },
          },
        });
      }
    } else {
      if (sendType === "partial" && input.qty !== undefined) {
        await sendPartial({
          variables: {
            input: {
              campaignId: campaign.id,
              qty: input.qty,
              sendWithVariantId: variantId,
            },
          },
        });
      } else {
        await sendFull({
          variables: {
            input: {
              campaignId: campaign.id,
              sendWithVariantId: variantId,
            },
          },
        });
      }
    }

    handleClose();
  }

  const now = DateTime.now();
  const scheduleFor = useWatch({ control, name: "scheduleFor" });
  const isScheduled = !!scheduleFor || !isInTextingHours;

  return (
    <Dialog open={true} onClose={handleClose} maxWidth="sm">
      <DialogTitle>
        Pick a Winner and Continue Sending {campaign.name}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <RhfDateTimePicker
            name="scheduleFor"
            dateLabel="Send Date"
            timeLabel="Send Time"
            minTime={now}
            minDate={now}
            control={control}
            optional={isInTextingHours}
            timezone={textingHours.timezone}
          />
          <Card sx={{ px: 2, py: 1, mb: 2 }}>
            <RhfSelect
              name="sendingOption"
              label="Sending Option"
              control={control}
              variant="underlined"
              fullWidth
              sx={{ mb: 2, pt: 1 }}
            >
              {variants.flatMap(variant => {
                const displayOrder = (variant.order ?? 0) + 1;
                return [
                  <MenuItem
                    key={`${variant.id}:full`}
                    value={`${variant.id}:full`}
                  >
                    Send variant {displayOrder} to the full list
                  </MenuItem>,
                  <MenuItem
                    key={`${variant.id}:partial`}
                    value={`${variant.id}:partial`}
                  >
                    Send variant {displayOrder} to a portion of the list, then
                    pause
                  </MenuItem>,
                ];
              })}
            </RhfSelect>
            {isPartial && (
              <RhfNumberField
                type="number"
                control={control}
                name="qty"
                fullWidth
                label="Texts to Send"
                sx={{ mb: 2 }}
                variant="standard"
              />
            )}
          </Card>
          <SendSummaryCard campaign={campaign} />

          <DialogActions
            sx={{ justifyContent: "flex-end", pl: 0, pr: 0, pt: 2 }}
          >
            <Button variant="outlined" onClick={handleClose}>
              CLOSE
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {isScheduled ? "SCHEDULE SEND" : "SEND NOW"}
            </Button>
          </DialogActions>
        </DialogContent>
      </form>
    </Dialog>
  );
}
